import { z } from 'zod';
import { refersTo } from '../common/refers-to';
import { DocumentCategoryGroupSchema, DocumentCategorySchema } from '../property/documents.schemas';

export const DATA_ROOM_RELEASE_BLOCK_REASONS = [
  'NOT_ACTIVE',
  'NOTICE_NOT_SENT',
  'NOT_CONCLUDED',
  'WITHDRAWAL_RECEIVED',
  'INSIDE_WITHDRAWAL_PERIOD',
  'NOTHING_MARKED',
] as const;
export const DataRoomReleaseBlockReasonSchema = z.enum(DATA_ROOM_RELEASE_BLOCK_REASONS);
export type DataRoomReleaseBlockReason = z.infer<typeof DataRoomReleaseBlockReasonSchema>;

// The Maklervertrag reasons a member may release past by acknowledging them (IMM-1207). All three are lifted
// (IMM-1207.2, IMM-1207.3); NOTICE_NOT_SENT stays a hard lock.
export const DATA_ROOM_OVERRIDABLE_REASONS = [
  'NOT_CONCLUDED',
  'WITHDRAWAL_RECEIVED',
  'INSIDE_WITHDRAWAL_PERIOD',
] as const satisfies ReadonlyArray<DataRoomReleaseBlockReason>;
export const DataRoomOverridableReasonSchema = z.enum(DATA_ROOM_OVERRIDABLE_REASONS);
export type DataRoomOverridableReason = z.infer<typeof DataRoomOverridableReasonSchema>;

export const DATA_ROOM_END_REASONS = ['WITHDRAWN', 'PROSPECT_LOST'] as const;
export type DataRoomEndReason = (typeof DATA_ROOM_END_REASONS)[number];

export const DataRoomReleaseSchema = z.object({
  state: z.enum(['ACTIVE', 'ENDED']),
  releasedAt: z.iso.datetime(),
  releasedByMemberId: refersTo('member', z.uuid()),
  // Null when that member is no longer active; the web app renders the „unknown member" placeholder.
  releasedByName: z.string().nullable(),
  endedAt: z.iso.datetime().nullable(),
  endReason: z.enum(DATA_ROOM_END_REASONS).nullable(),
  // The Maklervertrag reason the releasing member acknowledged; null for a release that passed the gate.
  overriddenReason: DataRoomOverridableReasonSchema.nullable(),
});

export const DATA_ROOM_DOCUMENT_STATUSES = ['SHARED', 'UNSHARED', 'DELETED'] as const;

export const DataRoomTrackingRowSchema = z.object({
  // A deleted document's id resolves to nothing; the row keeps its stored name.
  documentId: refersTo('document', z.uuid()),
  name: z.string(),
  status: z.enum(DATA_ROOM_DOCUMENT_STATUSES),
  firstViewedAt: z.iso.datetime().nullable(),
  lastViewedAt: z.iso.datetime().nullable(),
  lastDownloadedAt: z.iso.datetime().nullable(),
  downloadCount: z.number().int().nonnegative(),
});
export type DataRoomTrackingRow = z.infer<typeof DataRoomTrackingRowSchema>;

export type DataRoomTrackingSortColumn = 'name' | 'firstViewedAt' | 'lastViewedAt' | 'lastDownloadedAt';
export type DataRoomTrackingSortDirection = 'asc' | 'desc';

/** The card's default order and the server's: Zuletzt angesehen, newest first. */
export const DEFAULT_DATA_ROOM_TRACKING_SORT = { column: 'lastViewedAt', direction: 'desc' } as const satisfies {
  column: DataRoomTrackingSortColumn;
  direction: DataRoomTrackingSortDirection;
};

/** Times as instants, empty cells last in both directions, ties by name A–Z. One comparator for server and card. */
export function compareDataRoomTrackingRows(
  column: DataRoomTrackingSortColumn,
  direction: DataRoomTrackingSortDirection,
): (a: DataRoomTrackingRow, b: DataRoomTrackingRow) => number {
  const factor = direction === 'asc' ? 1 : -1;

  return (a, b) => {
    const byName = a.name.localeCompare(b.name, 'de');

    if (column === 'name') return byName * factor;

    const left = a[column];
    const right = b[column];

    // Emptiness is compared outside the direction factor, so empty cells stay last when reversed.
    if (left === null || right === null) return left === right ? byName : left === null ? 1 : -1;

    return (Date.parse(left) - Date.parse(right)) * factor || byName;
  };
}

export const DataRoomCardSchema = z.object({
  release: DataRoomReleaseSchema.nullable(),
  markedDocumentCount: z.number().int().nonnegative(),
  // Why a (re)release is refused right now without an acknowledgement; null when it may run — always null while active.
  blockedBy: DataRoomReleaseBlockReasonSchema.nullable(),
  // True when blockedBy is a Maklervertrag reason the member may release past by acknowledging it (IMM-1207).
  overridable: z.boolean(),
  // The last day of the Widerrufsfrist, set only with blockedBy INSIDE_WITHDRAWAL_PERIOD.
  withdrawalPeriodEndsOn: z.iso.date().nullable(),
  // Per document marked „Für Käufer“, plus per document the buyer opened that has since left the set; [] without a release.
  documents: z.array(DataRoomTrackingRowSchema),
});
export type DataRoomCard = z.infer<typeof DataRoomCardSchema>;

export const GetDataRoomPathParamsSchema = z.object({ workspaceId: z.uuid(), id: z.uuid() });
export const DataRoomWritePathParamsSchema = z.object({ organizationId: z.uuid(), id: z.uuid() });
export const DataRoomWriteBodySchema = z.object({}).strict();
// Release only; the withdrawal keeps DataRoomWriteBodySchema.
export const ReleaseDataRoomBodySchema = z
  .object({ acknowledgedReason: DataRoomOverridableReasonSchema.optional() })
  .strict();

// IMM-1329.2 — one Interessent of the dialog „Freigaben verwalten“: the card's gate, flattened to what the row needs.
export const DataRoomReleaseRowSchema = z.object({
  prospectId: refersTo('prospect', z.uuid()),
  organizationId: refersTo('organization', z.uuid()),
  // The Kontakt's display name, „{Vorname} {Nachname}“.
  name: z.string(),
  release: DataRoomReleaseSchema.pick({ state: true, releasedAt: true, endedAt: true, endReason: true }).nullable(),
  // As on DataRoomCardSchema: why a (re)release is refused right now without an acknowledgement; null while active.
  blockedBy: DataRoomReleaseBlockReasonSchema.nullable(),
  overridable: z.boolean(),
  withdrawalPeriodEndsOn: z.iso.date().nullable(),
  // The caller holds organization:deal:prospect-update at the row's Standort.
  canUpdate: z.boolean(),
});
export type DataRoomReleaseRow = z.infer<typeof DataRoomReleaseRowSchema>;

export const ListDataRoomReleasesPathParamsSchema = z.object({ workspaceId: z.uuid() });
export const ListDataRoomReleasesQuerySchema = z.object({ propertyId: z.uuid() });
// Rows sorted by name (German collation), then prospectId.
export const ListDataRoomReleasesResponseSchema = z.object({
  markedDocumentCount: z.number().int().nonnegative(),
  rows: z.array(DataRoomReleaseRowSchema),
});
export type ListDataRoomReleasesResponse = z.infer<typeof ListDataRoomReleasesResponseSchema>;

export const DataRoomReleaseChangeSchema = z.discriminatedUnion('action', [
  z
    .object({
      prospectId: z.uuid(),
      action: z.literal('RELEASE'),
      acknowledgedReason: DataRoomOverridableReasonSchema.optional(),
    })
    .strict(),
  z.object({ prospectId: z.uuid(), action: z.literal('WITHDRAW') }).strict(),
]);
export type DataRoomReleaseChange = z.infer<typeof DataRoomReleaseChangeSchema>;

export const SaveDataRoomReleasesPathParamsSchema = z.object({ organizationId: z.uuid() });
export const SaveDataRoomReleasesBodySchema = z
  .object({
    propertyId: z.uuid(),
    changes: z
      .array(DataRoomReleaseChangeSchema)
      .min(1)
      .max(200)
      .refine((changes) => new Set(changes.map((change) => change.prospectId)).size === changes.length, {
        message: 'one change per prospectId',
      }),
  })
  .strict();
export type SaveDataRoomReleasesBody = z.infer<typeof SaveDataRoomReleasesBodySchema>;

export const DataRoomReleaseChangeResultSchema = z.discriminatedUnion('outcome', [
  z.object({ prospectId: z.uuid(), outcome: z.literal('RELEASED'), reason: z.null() }),
  z.object({ prospectId: z.uuid(), outcome: z.literal('WITHDRAWN'), reason: z.null() }),
  z.object({ prospectId: z.uuid(), outcome: z.literal('UNCHANGED'), reason: z.null() }),
  // NOT_ACTIVE also covers an Interessent that is gone, Gewonnen, or not of this Objekt and Standort.
  z.object({ prospectId: z.uuid(), outcome: z.literal('FAILED'), reason: DataRoomReleaseBlockReasonSchema }),
]);
export type DataRoomReleaseChangeResult = z.infer<typeof DataRoomReleaseChangeResultSchema>;

// One row per change, in request order.
export const SaveDataRoomReleasesResponseSchema = z.object({ results: z.array(DataRoomReleaseChangeResultSchema) });
export type SaveDataRoomReleasesResponse = z.infer<typeof SaveDataRoomReleasesResponseSchema>;

/** The dialog „Freigaben verwalten“'s order (IMM-1329.2): name in German collation, then prospectId. One comparator for server and dialog. */
export function compareDataRoomReleaseRows(a: DataRoomReleaseRow, b: DataRoomReleaseRow): number {
  return a.name.localeCompare(b.name, 'de') || (a.prospectId < b.prospectId ? -1 : a.prospectId > b.prospectId ? 1 : 0);
}

export const DATA_ROOM_ACCESS_KINDS = ['VIEW', 'DOWNLOAD'] as const;
export type DataRoomAccessKind = (typeof DATA_ROOM_ACCESS_KINDS)[number];

export const BuyerDataRoomDocumentSchema = z.object({
  // No refersTo: public token-scoped echo, no list row for the buyer (ADR 0061 exemption).
  id: z.uuid(),
  displayName: z.string(),
  category: DocumentCategorySchema,
  categoryGroup: DocumentCategoryGroupSchema,
  contentType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  viewable: z.boolean(),
});

export const BuyerDataRoomSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('NONE') }),
  z.object({ state: z.literal('ENDED') }),
  z.object({ state: z.literal('ACTIVE'), documents: z.array(BuyerDataRoomDocumentSchema) }),
]);
export type BuyerDataRoom = z.infer<typeof BuyerDataRoomSchema>;

export const GetBuyerDataRoomBodySchema = z.object({ token: z.string().max(512) });
export const GetDataRoomDocumentUrlBodySchema = z.object({
  token: z.string().max(512),
  documentId: z.uuid(),
  kind: z.enum(DATA_ROOM_ACCESS_KINDS),
});
